import { Center, type CenterProps, Spinner } from '@chakra-ui/react';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { loadingImage, loadingImageDark } from '@revelio/assets';

import styles from './loading.module.css';

const SPINNER_SIZES: Record<string, string> = {
  sm: 'md',
  md: 'lg',
  lg: 'xl',
};

const GIF_WIDTHS: Record<string, number> = {
  sm: 85,
  md: 110,
  lg: 140,
};

const LoadingGifContext = createContext<string | undefined>(undefined);

export const LoadingGifProvider = ({
  gif,
  children,
}: {
  gif: string | undefined;
  children: ReactNode;
}) => (
  <LoadingGifContext.Provider value={gif}>
    {children}
  </LoadingGifContext.Provider>
);

export const useLoadingGif = () => useContext(LoadingGifContext);

export const Loading = ({ size = 'md' }: { size?: string } = {}) => {
  const loadingGif = useLoadingGif();

  if (!loadingGif) {
    return (
      <Center data-testid="loading" py={8}>
        <Spinner
          thickness="4px"
          speed="0.7s"
          color="green.500"
          emptyColor="gray.200"
          size={SPINNER_SIZES[size] ?? 'lg'}
        />
      </Center>
    );
  }

  const darkGif = loadingGif === loadingImage ? loadingImageDark : undefined;
  const dimensionStyle = {
    margin: 'auto',
    width: `${GIF_WIDTHS[size] ?? 110}px`,
    height: 'auto',
  } as const;

  return (
    <>
      <img
        data-testid="loading"
        className={`${styles.gif}${darkGif ? ` ${styles.withDark}` : ''}`}
        style={dimensionStyle}
        src={loadingGif}
        alt="loading"
      />
      {darkGif && (
        <img
          aria-hidden
          className={styles.gifDark}
          style={dimensionStyle}
          src={darkGif}
          alt=""
        />
      )}
    </>
  );
};

interface ICenterLoading extends CenterProps {
  className?: string;
  size?: string;
  shouldShow?: boolean;
  testId?: string;
}

export const CenterLoading = ({
  className,
  size,
  shouldShow = true,
  testId = 'data-loading',
  ...chakraProps
}: ICenterLoading) => {
  const display = useMemo(() => (shouldShow ? 'flex' : 'none'), [shouldShow]);

  return (
    <Center
      data-testid={testId}
      className={className}
      height="100%"
      width="100%"
      display={display}
      {...chakraProps}
    >
      <Loading size={size} />
    </Center>
  );
};

export const CenterSpinner = () => (
  <Center height="100%" width="100%">
    <Spinner color="navyBlue.500" />
  </Center>
);

export default Loading;
